import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { formSubmissionsApi } from '../../../services/api'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import AdminFormField, { adminInputClass } from '../../../components/admin/AdminFormField'
import { formatDateTime } from '../../../utils/date'
import { useMarkNotificationsRead } from '../../../hooks/useMarkNotificationsRead'
import { useAuth } from '../../../context/AuthContext'
import { CONTACT_BRANCH_KEYS, CONTACT_SUBJECT_KEYS } from '../../../constants/contact'

const STATUSES = ['new', 'reviewed', 'replied', 'archived']

function formatSubject(subject, t) {
  if (!subject) return '—'
  if (CONTACT_SUBJECT_KEYS.includes(subject)) {
    return t(`contact.form.subjects.${subject}`)
  }
  return subject
}

function formatBranch(branch, t) {
  if (!branch) return '—'
  if (CONTACT_BRANCH_KEYS.includes(branch)) {
    return t(`contact.form.branches.${branch}`)
  }
  return branch
}

export default function AdminContactSubmissionDetail() {
  const { id } = useParams()
  const { t } = useTranslation()
  const { hasModuleAccess } = useAuth()
  const canEdit = hasModuleAccess('forms_contact', 'can_edit')
  useMarkNotificationsRead('forms_contact', id ? Number(id) : null)

  const [submission, setSubmission] = useState(null)
  const [status, setStatus] = useState('new')
  const [adminNotes, setAdminNotes] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const load = () => {
    setLoading(true)
    formSubmissionsApi
      .getContact(id)
      .then(({ data }) => {
        const item = data.data || data
        setSubmission(item)
        setStatus(item.status || 'new')
        setAdminNotes(item.admin_notes || '')
      })
      .catch(() => setSubmission(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [id])

  const handleSave = async () => {
    if (!canEdit) return

    setSaving(true)
    try {
      await formSubmissionsApi.updateContact(id, { status, admin_notes: adminNotes })
      load()
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingSpinner className="py-20" />
  if (!submission) return null

  const detailFields = [
    ['reference', submission.reference_number || submission.id],
    ['name', submission.name],
    ['email', submission.email],
    ['phone', submission.phone],
    ['subject', formatSubject(submission.subject, t)],
    ['branch', formatBranch(submission.branch, t)],
    ['message', submission.message],
    ['date', submission.created_at ? formatDateTime(submission.created_at) : '—'],
  ]

  return (
    <div>
      <Link
        to="/cp-login/forms/contact"
        className="mb-6 inline-flex items-center gap-2 text-sm text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('admin.forms.backToList')}
      </Link>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-primary">{submission.name}</h2>
        <p className="text-text/60">{submission.reference_number || `#${submission.id}`}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h3 className="mb-4 font-semibold text-primary">{t('admin.forms.details')}</h3>
          <dl className="grid gap-4 sm:grid-cols-2">
            {detailFields.map(([key, value]) => (
              <div key={key} className={key === 'message' ? 'sm:col-span-2' : ''}>
                <dt className="text-xs font-medium uppercase tracking-wide text-text/50">
                  {t(`admin.forms.${key}`)}
                </dt>
                <dd className="mt-1 whitespace-pre-wrap text-sm text-text">{value || '—'}</dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card>
          <h3 className="mb-4 font-semibold text-primary">{t('admin.forms.status')}</h3>
          {canEdit ? (
            <div className="space-y-4">
              <AdminFormField label={t('admin.forms.status')} htmlFor="contact-status">
                <select
                  id="contact-status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className={adminInputClass}
                >
                  {STATUSES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </AdminFormField>
              <AdminFormField label={t('admin.forms.adminNotes')} htmlFor="contact-admin-notes">
                <textarea
                  id="contact-admin-notes"
                  rows={4}
                  value={adminNotes}
                  onChange={(event) => setAdminNotes(event.target.value)}
                  className={adminInputClass}
                />
              </AdminFormField>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? t('common.loading') : t('admin.forms.saveStatus')}
              </Button>
            </div>
          ) : (
            <p className="text-sm font-medium capitalize text-text">{submission.status}</p>
          )}
        </Card>
      </div>
    </div>
  )
}
